import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@app/auth/services/auth.service';
import { ApiService } from '@app/core/services/api.service';
import { CallQualitySummary } from '@app/phone/models/phone.models';
import { CallQualityService } from '@app/phone/services/call-quality.service';
import { ShowError } from '@app/shared/interceptors/error-interceptor';
import { concat, filter, firstValueFrom, from, map, Observable, startWith, Subject } from 'rxjs';

interface CallQualitySummaryResponse {
  callId: string;
  summary: CallQualitySummary;
}

/**
 * Caches the whole-call quality summary computed by CallQualityService, keyed by the call's own
 * CallId, and makes it available to the call history detail view.
 *
 * Stored immediately on receipt rather than correlated to a CallHistory record up front: the
 * summary is known synchronously, but the matching CDR arrives asynchronously and isn't guaranteed
 * to exist yet, so waiting to store it would risk losing it. Correlation instead happens lazily, at
 * read time in observeQualitySummary, once the caller already has the real CallHistory record (and
 * therefore both of its ids) in hand.
 *
 * Matching is by prefix, not exact equality: a CallHistory record's origCallid/termCallid can be a
 * truncated copy of the full CallId (observed truncated to 20 characters, likely a backend column
 * length limit) rather than the same string, so the CallId is checked for starting with whichever
 * CallHistory id is being looked up.
 *
 * Follows the same in-memory-cache + Subject pattern as CallTranscriptionService rather than
 * writing to IndexedDB: a quality summary is computed entirely client-side from local WebRTC
 * stats, so the in-memory cache is the source of truth for the current session; the POST below
 * persists it to the backend for later retrieval (e.g. after a reload), fire-and-forget, since the
 * client already has the authoritative value and there's no response to wait on. There's no GET
 * fallback yet for a cache miss (e.g. a summary from a previous session) -- see the TODO in
 * observeQualitySummary.
 */
@Injectable({
  providedIn: 'root',
})
export class CallQualitySummaryService extends ApiService {
  protected path = 'users/{me}/call-quality';

  private readonly summaries = new Map<string, CallQualitySummary>();
  private readonly summarySubject = new Subject<{ callId: string; summary: CallQualitySummary }>();

  constructor(
    private callQualityService: CallQualityService,
    private authService: AuthService
  ) {
    super();

    this.callQualityService.summaryReady$.subscribe(({ callId, summary }) => {
      this.summaries.set(callId, summary);
      this.summarySubject.next({ callId, summary });
      void this.persistSummary(callId, summary);
    });

    this.authService.jwtClaims$.subscribe((claims) => {
      if (!claims) {
        this.summaries.clear();
      }
    });
  }

  /**
   * Emits the quality summary whose CallId starts with either id of a CallHistory record, replaying
   * a cached value to a late subscriber. Emits `undefined` if no summary is known for this call.
   *
   * Falls back to a GET on a cache miss (e.g. a call from a previous session that's no longer in the
   * in-memory cache) -- keyed by origCallId and termCallId.
   */
  public observeQualitySummary(origCallId: string, termCallId: string): Observable<CallQualitySummary | undefined> {
    const matches = (callId: string): boolean =>
      (origCallId.length > 0 && callId.startsWith(origCallId)) ||
      (termCallId.length > 0 && callId.startsWith(termCallId));

    let cached: CallQualitySummary | undefined;
    for (const [callId, summary] of this.summaries) {
      if (matches(callId)) {
        cached = summary;
        break;
      }
    }

    const live$ = this.summarySubject.pipe(
      filter((event) => matches(event.callId)),
      map((event) => event.summary)
    );

    if (cached) {
      return live$.pipe(startWith(cached));
    }

    return concat(from(this.fetchSummary(origCallId, termCallId)), live$);
  }

  /** Persists the summary to the backend, fire-and-forget -- the client already has the authoritative value, no response to wait on. */
  private async persistSummary(callId: string, summary: CallQualitySummary): Promise<void> {
    try {
      await firstValueFrom(this.post(this.path, { callId, summary }));
    } catch (error) {
      console.error(`Failed to persist call quality summary for ${callId}`, error);
    }
  }

  /** Fetches a summary from the backend on a cache miss, caching the result so a later subscriber doesn't re-fetch. */
  private async fetchSummary(origCallId: string, termCallId: string): Promise<CallQualitySummary | undefined> {
    try {
      const response = await firstValueFrom(
        this.get<CallQualitySummaryResponse>(this.path, {
          params: { origCallid: origCallId, termCallid: termCallId },
          context: new HttpContext().set(ShowError, (err) => err.status !== 404),
        })
      );
      this.summaries.set(origCallId, response.summary);
      return response.summary;
    } catch (error) {
      if ((error as HttpErrorResponse).status !== 404) {
        console.error(`Failed to fetch call quality summary for ${origCallId}`, error);
      }
      return undefined;
    }
  }
}
